/* MGM shared room. One saved room that every planner (irrigation, light layout, fan layout, dehumidifier) reads
   and writes. Stored in the browser (localStorage); export/import as JSON; import from a CM Horticulture facility
   configurator PDF (the text summary it prints). All geometry in feet, origin at the room's top-left, x along length.
   Room shape: { version, name, units, room:{length,width,height}, layout:'single'|'racks',
                 canopies:[{x,y,length,width,tiers,tierSpacing,plantHeight,label}], source:{type,summary}, updatedAt } */
(function () {
  var KEY = 'mgm_room', LIST = 'mgm_rooms';
  var RACK_DEPTH_FT = 4, DEFAULT_TIER_SPACING_FT = 4.0, DEFAULT_PLANT_HEIGHT_IN = 36;

  function safeGet(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { return null; } }
  function safeSet(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); return true; } catch (e) { return false; } }

  function blank() {
    return { version: 1, name: '', units: 'ft', room: { length: 0, width: 0, height: 0 }, layout: 'single', canopies: [], source: { type: 'manual', summary: '' }, updatedAt: null };
  }
  function area(room) { return (room.canopies || []).reduce(function (a, c) { return a + c.length * c.width * (c.tiers || 1); }, 0); }
  function footprint(room) { return (room.canopies || []).reduce(function (a, c) { return a + c.length * c.width; }, 0); }
  function validate(room) {
    var w = [];
    var r = room.room || {};
    if (!(r.length > 0 && r.width > 0)) w.push('Room length and width are required.');
    if (r.length > 300 || r.width > 300) w.push('Room dimensions over 300 ft are not supported.');
    (room.canopies || []).forEach(function (c, i) {
      if (c.x < 0 || c.y < 0 || c.x + c.length > r.length + 0.01 || c.y + c.width > r.width + 0.01) w.push('Canopy ' + (i + 1) + ' sits outside the room.');
      if ((c.tiers || 1) > 1 && c.tierSpacing * c.tiers > r.height + 0.01) w.push('Canopy ' + (i + 1) + ': tiers are taller than the ceiling.');
    });
    return w;
  }

  /* ---- CM Horticulture configurator PDF: parse the printed summary ---- */
  function num(s) { return parseFloat(String(s).replace(/[^0-9.\-]/g, '')); }
  function inchesOrFeet(s) { // "20.25\" each side" -> ft ; "3'" -> ft ; "16.5\"" -> ft
    var m = String(s).match(/([0-9.]+)\s*(["'])/); if (!m) return num(s);
    return m[2] === '"' ? num(m[1]) / 12 : num(m[1]);
  }
  function parseCMText(text) {
    var t = text.replace(/\s+/g, ' ');
    if (!/cmhort|facility configurator/i.test(t)) return null;
    var room = blank();
    room.source = { type: 'cm-pdf', summary: t.slice(0, 400) };
    var mTitle = t.match(/^(.*?)\s+(Vertical rack system|MaxRoll|.*?bench.*?)\s+\w+ \d{1,2}, \d{4}/i);
    room.name = (t.match(/^(.*?)\s+(?:Vertical|MaxRoll|Rolling|Stationary|Mobile)/i) || [, 'Imported room'])[1].trim().slice(0, 60);
    var mRoom = t.match(/ROOM\s+([0-9.]+)'\s*L\s*[×x]\s*([0-9.]+)'\s*W\s*[×x]\s*([0-9.]+)'\s*H/i);
    if (!mRoom) return null;
    room.room = { length: num(mRoom[1]), width: num(mRoom[2]), height: num(mRoom[3]) };
    var L = room.room.length, W = room.room.width;
    var mBench = t.match(/BENCHES\s+(\d+)\s+BENCH SIZE\s+([0-9.]+)'\s*[×x]\s*([0-9.]+)'/i);
    var mRack = t.match(/ROWS\s+(\d+)\s+RACK SECTIONS\s+(\d+)\s+GROW TIERS\s+(\d+)/i);
    if (mBench) {
      var n = parseInt(mBench[1], 10), bw = num(mBench[2]), bl = num(mBench[3]);
      var gap = (t.match(/NEUTRAL GAP\s+([0-9.]+")/i) || [])[1], wall = (t.match(/WALL GAPS L \/ R\s+([0-9.]+")/i) || [])[1];
      var gapFt = gap ? inchesOrFeet(gap) : 0, wallFt = wall ? inchesOrFeet(wall) : (W - n * bw - (n - 1) * gapFt) / 2;
      var heightIn = (t.match(/BENCH HEIGHT\s+([0-9.]+")/i) || [])[1];
      room.layout = 'single';
      var y0 = Math.max(0, (L - bl) / 2);
      for (var i = 0; i < n; i++) {
        room.canopies.push({ x: +y0.toFixed(2), y: +(wallFt + i * (bw + gapFt)).toFixed(2), length: bl, width: bw, tiers: 1, tierSpacing: 0, plantHeight: DEFAULT_PLANT_HEIGHT_IN, label: 'Bench ' + (i + 1), benchHeightIn: heightIn ? num(heightIn) : null });
      }
      room.source.exact = true;
    } else if (mRack) {
      var rows = parseInt(mRack[1], 10), sections = parseInt(mRack[2], 10), tiers = parseInt(mRack[3], 10);
      var foot = num((t.match(/FLOOR FOOTPRINT\s+([0-9,\.]+)\s*ft/i) || [, '0'])[1]);
      var rackH = num((t.match(/RACK HEIGHT\s+([0-9.]+)'/i) || [, '0'])[1]);
      var aisle = (t.match(/OPEN AISLES\s+(\d+)\s*[×x]\s*([0-9.]+)'/i) || [, '1', '3']);
      var aisleN = parseInt(aisle[1], 10), aisleFt = num(aisle[2]);
      var perRow = Math.ceil(sections / rows), secArea = foot && sections ? foot / sections : RACK_DEPTH_FT * 8;
      var secLen = +(secArea / RACK_DEPTH_FT).toFixed(2), rowLen = Math.min(L, +(perRow * secLen).toFixed(2));
      var tierSpacing = tiers > 1 && rackH ? +(rackH / tiers).toFixed(2) : DEFAULT_TIER_SPACING_FT;
      var used = rows * RACK_DEPTH_FT + aisleN * aisleFt, margin = Math.max(0, (W - used) / 2);
      var y = margin, aisleAfter = Math.floor(rows / 2);
      room.layout = 'racks';
      for (var r = 0; r < rows; r++) {
        room.canopies.push({ x: +((L - rowLen) / 2).toFixed(2), y: +y.toFixed(2), length: rowLen, width: RACK_DEPTH_FT, tiers: tiers, tierSpacing: tierSpacing, plantHeight: 18, label: 'Rack row ' + (r + 1) });
        y += RACK_DEPTH_FT + (r + 1 === aisleAfter && aisleN ? aisleFt : 0);
      }
      room.source.exact = false;
      room.source.note = 'Rack positions reconstructed from the configurator summary (rows, sections, tiers, footprint). Adjust if needed.';
    } else {
      return null;
    }
    room.updatedAt = new Date().toISOString();
    return room;
  }

  /* pdf.js text extraction (lazy loaded from cdnjs) */
  function loadPdfJs() {
    if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
    return new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      s.onload = function () { window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'; res(window.pdfjsLib); };
      s.onerror = function () { rej(new Error('PDF reader did not load')); };
      document.head.appendChild(s);
    });
  }
  function importPdf(file) {
    return loadPdfJs().then(function (pdfjs) { return file.arrayBuffer(); })
      .then(function (buf) { return window.pdfjsLib.getDocument({ data: buf }).promise; })
      .then(function (doc) {
        var pages = [];
        for (var i = 1; i <= Math.min(doc.numPages, 3); i++) pages.push(doc.getPage(i).then(function (p) { return p.getTextContent(); }).then(function (tc) { return tc.items.map(function (it) { return it.str; }).join(' '); }));
        return Promise.all(pages);
      })
      .then(function (texts) {
        var room = parseCMText(texts.join(' '));
        if (!room) throw new Error('This PDF is not a CM Horticulture configurator export, or its room line could not be read.');
        return room;
      });
  }

  /* ---- persistence ---- */
  function current() { return safeGet(KEY) || blank(); }
  function save(room) {
    room.updatedAt = new Date().toISOString();
    safeSet(KEY, room);
    var list = safeGet(LIST) || [];
    if (room.name) {
      list = list.filter(function (r) { return r.name !== room.name; });
      list.unshift(room);
      safeSet(LIST, list.slice(0, 20));
    }
    document.dispatchEvent(new CustomEvent('mgm:room', { detail: room }));
    return room;
  }
  function list() { return safeGet(LIST) || []; }
  function remove(name) { safeSet(LIST, list().filter(function (r) { return r.name !== name; })); }
  function exportJson(room) { return JSON.stringify(room, null, 2); }
  function importJson(text) { var r = JSON.parse(text); if (!r || !r.room) throw new Error('Not a room file'); r.version = 1; return r; }

  /* ---- drawing: top-down SVG, 1 ft grid ---- */
  function svg(room, opts) {
    opts = opts || {};
    var L = room.room.length || 10, W = room.room.width || 10, pad = 1;
    var vw = L + pad * 2, vh = W + pad * 2;
    var parts = ['<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + vw + ' ' + vh + '" class="mgm-room-svg" role="img" aria-label="Room drawing">'];
    parts.push('<rect x="' + pad + '" y="' + pad + '" width="' + L + '" height="' + W + '" class="mgm-room-svg__room"/>');
    for (var g = 5; g < L; g += 5) parts.push('<line x1="' + (pad + g) + '" y1="' + pad + '" x2="' + (pad + g) + '" y2="' + (pad + W) + '" class="mgm-room-svg__grid"/>');
    for (g = 5; g < W; g += 5) parts.push('<line x1="' + pad + '" y1="' + (pad + g) + '" x2="' + (pad + L) + '" y2="' + (pad + g) + '" class="mgm-room-svg__grid"/>');
    (room.canopies || []).forEach(function (c, i) {
      parts.push('<rect x="' + (pad + c.x) + '" y="' + (pad + c.y) + '" width="' + c.length + '" height="' + c.width + '" class="mgm-room-svg__canopy' + ((c.tiers || 1) > 1 ? ' is-rack' : '') + '"/>');
      parts.push('<text x="' + (pad + c.x + c.length / 2) + '" y="' + (pad + c.y + c.width / 2) + '" class="mgm-room-svg__label" text-anchor="middle" dominant-baseline="middle" font-size="' + Math.max(0.8, Math.min(1.6, c.width * 0.35)) + '">' + (c.label || 'Canopy ' + (i + 1)) + ((c.tiers || 1) > 1 ? ' x' + c.tiers : '') + '</text>');
    });
    parts.push('<text x="' + (pad + L / 2) + '" y="' + (pad - 0.25) + '" text-anchor="middle" font-size="1" class="mgm-room-svg__dim">' + L + ' ft</text>');
    parts.push('<text x="' + (pad - 0.3) + '" y="' + (pad + W / 2) + '" text-anchor="middle" font-size="1" class="mgm-room-svg__dim" transform="rotate(-90 ' + (pad - 0.3) + ' ' + (pad + W / 2) + ')">' + W + ' ft</text>');
    parts.push('</svg>');
    return parts.join('');
  }

  window.MGMRoom = { blank: blank, current: current, save: save, list: list, remove: remove, exportJson: exportJson, importJson: importJson, importPdf: importPdf, parseCMText: parseCMText, validate: validate, area: area, footprint: footprint, svg: svg };
})();
